import {v4 as uuidv4} from 'uuid';
import fuzzysort from 'fuzzysort';

export function toTitleCase(input) {
  if (input === '') return input;
  return input.split(' ')
    .map((word) => (word !== '' ? word[0].toUpperCase() + word.slice(1) : ''))
    .join(' ');
}

// @see https://learnersbucket.com/examples/javascript/how-to-format-phone-number-in-javascript/
export function formatPhoneNumber(phone) {
  // Filter only numbers from the input
  const cleaned = (`${phone}`).replace(/\D/g, ''),
    // Check if the input is of correct length
        match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    // Start with 2, drop leading 1 if present
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }

  return phone;
}

export function slugify(text) {
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(/[^\w-]+/g, '') // Remove all non-word characters
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

/**
 * Naive implementation, suitable for average words
 * @see https://www.quickanddirtytips.com/education/grammar/a-versus-an for instance
 * @param {string} followingWord - the object following the indefinite article
 * @return {string} - a or an
 */
export function indefiniteArticle(followingWord) {
  return followingWord && /^[aieouAIEOU].*/.test(followingWord) ? 'an' : 'a';
}

export function isString(value) {
  return typeof value === 'string';
}

/**
 * Format as a currency
 * @param number
 * @return {string}
 */
export function asCurrency(number, options = {sign: false}) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // Use the same rounding method as Python 3 does (i.e., round ties to the nearest even least significant digit).
    // JS defaults to rounding away from 0 (!)
    roundingMode: 'halfEven',
  });
  if (options.sign) {
    const parts = formatter.formatToParts(number);
    return parts.filter((part) => part.type !== 'currency').map((part) => part.value).join('');
  }
  return formatter.format(number);
}

/**
 * Add commas
 * @see https://docs.djangoproject.com/en/4.0/ref/contrib/humanize/#intcomma if you are in django template context - django equivalent filter
 * @param number
 * @return {string}
 */
export function formatInteger(number) {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
  }).format(number);
}

/**
 * Transforms an integer value into its alphabetical equivalent
 * @example
 * intToChar(0) -> 'a'
 * intToChar(1) -> 'b'
 * intToChar(1, true) -> 'B'
 *
 * @param int {Number} the integer you want to convert
 * @param toCapital {Boolean} want a capital letter?
 * @returns {String} the character that represents the integer
 */
export function intToChar(int, toCapital = false) {
  // Will either be 65 for capital letters or 97 for lowercase letters
  const charCode = toCapital ? 'A'.charCodeAt(0) : 'a'.charCodeAt(0);

  return String.fromCharCode(charCode + int);
}

/**
 * Same as above but reverse
 * @param char the character you want to convert
 * @param toCapital are we converting from Capitals?
 * @return {Number}  the integer that represents the character
 */
export function charToInt(char, toCapital = false) {
  // Will either be 65 for capital letters or 97 for lowercase letters
  const charCode = toCapital ? 'A'.charCodeAt(0) : 'a'.charCodeAt(0);
  return char.charCodeAt(0) - charCode;
}

export function generateUUID() {
  return uuidv4();
}

/**
 * turns a number string into a proper number type
 * @param {String} value: a string that is a number or float
 * @returns {Number} the parsed number or float, or returns 0 if the value was NaN
 */
export function toNumber(value) {
  if (!isString(value)) return value; // short circuit if its not a string
  const valueAsNumber = Number(value);
  return Number.isNaN(valueAsNumber) ? 0 : valueAsNumber;
}

export function formatAddress(street, city, state, zip) {
  let address = '';

  if (street) {
    address += street;
  }

  if (city) {
    address += `${street ? ', ' : ''}${city}`;
  }

  if (state) {
    address += `${street || city ? ', ' : ''}${state}`;
  }

  if (zip) {
    address += `${street || city || state ? ' ' : ''}${zip}`;
  }

  return address.trim();
}

/**
 * Naive implementation of pluralization but should cover all our needs for Canopy.
 * If not we should use a library
 * Generated with ChatGPT
 * @param word
 * @returns {*|string}
 */
export function pluralize(word) {
  if (!isString(word)) return word; // don't even try...
  const irregularPlurals = {
    // Add irregular plurals here if needed
    child: 'children',
    person: 'people',
    ox: 'oxen',
    goose: 'geese',
    mouse: 'mice',
    man: 'men',
    woman: 'women',
  },

        specialCases = [
          // Add special cases (ending with "is", "on", "um", etc.) here if needed
          {pattern: /is$/, plural: 'es'},
          {pattern: /on$/, plural: 'a'},
          {pattern: /um$/, plural: 'a'},
        ],

        lowercaseWord = word.toLowerCase();

  if (irregularPlurals[lowercaseWord] !== undefined) {
    return irregularPlurals[lowercaseWord];
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const {pattern, plural} of specialCases) {
    if (pattern.test(lowercaseWord)) {
      return word.replace(pattern, plural);
    }
  }

  if (lowercaseWord.endsWith('y')) {
    return `${word.slice(0, -1)}ies`;
  } if (lowercaseWord.endsWith('s') || lowercaseWord.endsWith('x') || lowercaseWord.endsWith('z') || lowercaseWord.endsWith('ch') || lowercaseWord.endsWith('sh')) {
    return `${word}es`;
  }
  return `${word}s`;
}

/**
 * Implementation of fuzzy search using the fuzzy sort library. Use this for for searching through and array
 * of objects
 * @param searchText {String} the search text to search for
 * @param options {Array[Object]} the array of objects to search through
 * @param searchKey {String} the key to search on
 * @param limit {Number} the maximum number of results to return
 * @param threshold {Number} The threshold for search results, the lower the number the less strict the search
 */
export function fuzzySearch(searchText, options, searchKey, limit = 25, threshold = 0.1) {
  const results = fuzzysort.go(searchText, options, {
    key: searchKey,
    limit,
    threshold,
  });
  return results.map((result) => result.obj);
}
